import React from "react";
import { cn } from "@/lib/utils";
import { CompetitorFavicon, InfoPopover } from "@/components/vb";
import { TRUST_SIGNALS_TOTAL } from "@/types/vb";
import type { CompanyExposure } from "./verdictModel";

/** Focus company vs the buyer's tracked competitors on AI Trust Signal exposure:
 *  how many of the 19 signals each company surfaced when buyers asked AI. A
 *  coverage bar per company (focus highlighted), in the aits-2.0 bar vocabulary.
 *  The judge names many other companies too; we compare only the chosen
 *  competitors so the view stays on-message. */
export function CompetitorSignalCompare({ rows }: { rows: CompanyExposure[] }) {
  // Nothing to compare against (no competitors tracked / surfaced).
  if (rows.length < 2) return null;

  return (
    <section className="vb-compare">
      <div className="vb-compare__hd">
        <span className="vb-compare__lbl">AI Trust Signals vs competitors</span>
        <InfoPopover label="About AI Trust Signals vs competitors">
          For you and each competitor you track: how many of your {TRUST_SIGNALS_TOTAL} AI
          Trust Signals surfaced in AI answers (the bar), and the share of AI answers the company
          showed up in at all ("shown"). Higher is better on both.
        </InfoPopover>
      </div>
      <ul className="vb-compare__rows">
        {rows.map((c) => {
          const widthPct = Math.round((c.signalsExposed / TRUST_SIGNALS_TOTAL) * 100);
          return (
            <li className={cn("vb-compare-row", c.isFocus && "is-focus")} key={c.name}>
              <span className="vb-compare-row__co">
                <CompetitorFavicon name={c.name} url={c.url} size={16} />
                <span className="vb-compare-row__name" title={c.name}>
                  {c.name}
                </span>
                {c.isFocus && <span className="vb-compare-row__you">You</span>}
              </span>
              {c.appeared ? (
                <>
                  <span
                    className="vb-compare-row__bar"
                    role="img"
                    aria-label={`${c.signalsExposed} of ${TRUST_SIGNALS_TOTAL} AI Trust Signals`}
                  >
                    <span
                      className={cn(c.isFocus && "is-focus")}
                      style={{ width: `${widthPct}%` }}
                    />
                  </span>
                  <span className="vb-compare-row__stats">
                    <span className="vb-compare-row__n">
                      <span className="tnum">
                        {c.signalsExposed}/{TRUST_SIGNALS_TOTAL}
                      </span>{" "}
                      signals
                    </span>
                    {c.visibility != null && (
                      <span className="vb-compare-row__pct tnum">
                        {Math.round(c.visibility * 100)}% shown
                      </span>
                    )}
                  </span>
                </>
              ) : (
                <span className="vb-compare-row__none">Not substantively mentioned by AI</span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default CompetitorSignalCompare;
